import { Button, Stack } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogPromptScoringLevel } from '../../api/DialogPromptApi';
import { IUser, ScoringLevel, UserApi } from '../../api/UserApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { getAudioByText } from './DialogPromptComponent';

interface DialogPromptScoringLevelComponentProps {
    dialogPrompt: DialogPromptScoringLevel;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptScoringLevelComponent: React.FC<DialogPromptScoringLevelComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [response, setResponse] = React.useState<string | null>(null);

    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [chatBubbleDone2, setChatBubbleDone2] = React.useState(false);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);

    const updateDbScoringLevel = React.useCallback((scoringLevel: ScoringLevel) => {
        const updatedUser: IUser = {
            scoring_level: scoringLevel,
        };
        // Call the update function
        UserApi.update(updatedUser)
            .then(() => {
                // Handle the response
                // console.log('User updated:', response.data);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    }, [setErrorMessage, history, location]);

    const handleYesButtonClick = React.useCallback(() => {
        setResponse(dialogPrompt.yes_response);
        updateDbScoringLevel(ScoringLevel.Hard);
    }, [dialogPrompt.yes_response, updateDbScoringLevel]);

    const handleNoButtonClick = React.useCallback(() => {
        setResponse(dialogPrompt.no_response);
        updateDbScoringLevel(ScoringLevel.Easy);
    }, [dialogPrompt.no_response, updateDbScoringLevel]);

    const handleMaybeButtonClick = React.useCallback(() => {
        setResponse(dialogPrompt.maybe_response);
        updateDbScoringLevel(ScoringLevel.Medium);
    }, [dialogPrompt.maybe_response, updateDbScoringLevel]);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
    }, [clearFooterList, dialogPrompt.prompt, history, setErrorMessage]);

    React.useEffect(() => {
        if (chatBubbleDone) {
            setActionList([
                <Button variant="contained" color="primary" onClick={() => handleYesButtonClick()}>
                    {dialogPrompt.yes_button_text}
                </Button>,
                <Button variant="contained" color="primary" onClick={() => handleMaybeButtonClick()}>
                    {dialogPrompt.maybe_button_text}
                </Button>,
                <Button variant="contained" color="secondary" onClick={() => handleNoButtonClick()}>
                    {dialogPrompt.no_button_text}
                </Button>
            ])
        }
    }, [chatBubbleDone, dialogPrompt.maybe_button_text, dialogPrompt.no_button_text, dialogPrompt.yes_button_text, handleMaybeButtonClick, handleNoButtonClick, handleYesButtonClick, setActionList]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    })

    React.useEffect(() => {
        if (chatBubbleDone2) {
            setDialogPromptComplete(dialogPrompt.id)
        }
    }, [chatBubbleDone2, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {response == null ? null : (
                <ChatBubble
                    isTeacher={!dialogPrompt.is_teacher}
                    message={response}
                    messageAudio={null}  // no audio for speech by students
                    showAvatar={showAvatar}
                    setChatBubbleDone={setChatBubbleDone2}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            )}
        </Stack>
    );
};

export default DialogPromptScoringLevelComponent;
